import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";
import { Palette, Save, PlusCircle, Trash2 } from "lucide-react";

const FIELD_GROUPS = [
  {
    key: "identity", label_fr: "Identité visuelle", label_en: "Visual identity",
    fields: [
      { name: "primary_color", label_fr: "Couleur primaire", label_en: "Primary color", type: "color" },
      { name: "secondary_color", label_fr: "Couleur secondaire", label_en: "Secondary color", type: "color" },
      { name: "accent_color", label_fr: "Accent", label_en: "Accent", type: "color" },
      { name: "font_family", label_fr: "Police", label_en: "Font", type: "text" },
      { name: "logo_url", label_fr: "URL du logo", label_en: "Logo URL", type: "text" },
    ],
  },
  {
    key: "cover", label_fr: "Page couverture", label_en: "Cover page",
    fields: [
      { name: "cover_title", label_fr: "Titre de couverture", label_en: "Cover title", type: "text" },
      { name: "cover_subtitle", label_fr: "Sous-titre", label_en: "Subtitle", type: "text" },
      { name: "watermark", label_fr: "Filigrane", label_en: "Watermark", type: "text" },
      { name: "qr_url", label_fr: "URL du QR Code", label_en: "QR Code URL", type: "text" },
    ],
  },
  {
    key: "footer", label_fr: "Pied de page & mentions", label_en: "Footer & mentions",
    fields: [
      { name: "footer_text", label_fr: "Texte de pied de page", label_en: "Footer text", type: "text" },
      { name: "legal_notice", label_fr: "Mentions légales", label_en: "Legal notice", type: "textarea" },
      { name: "signature_name", label_fr: "Nom du signataire", label_en: "Signatory name", type: "text" },
      { name: "signature_role", label_fr: "Rôle / titre", label_en: "Signatory role", type: "text" },
      { name: "contact_email", label_fr: "Email de contact", label_en: "Contact email", type: "text" },
      { name: "contact_phone", label_fr: "Téléphone", label_en: "Phone", type: "text" },
    ],
  },
];

const SECTOR_LABELS = {
  orientation_professionnelle: { fr: "Orientation professionnelle", en: "Career orientation" },
  employability: { fr: "Employabilité", en: "Employability" },
  immigration: { fr: "Immigration", en: "Immigration" },
  social_accompaniment: { fr: "Accompagnement social", en: "Social accompaniment" },
  hr: { fr: "RH", en: "HR" },
  training: { fr: "Formation", en: "Training" },
  banking: { fr: "Banque", en: "Banking" },
  insurance: { fr: "Assurance", en: "Insurance" },
  government: { fr: "Gouvernement", en: "Government" },
  annual_report: { fr: "Rapport annuel", en: "Annual report" },
  funders: { fr: "Bailleurs de fonds", en: "Funders" },
  custom: { fr: "Sur mesure", en: "Custom" },
};

export default function WhiteLabelStudio() {
  const { lang } = useI18n();
  const [branding, setBranding] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [newTpl, setNewTpl] = useState({ name: "", sector: "employability", report_type: "" });

  const load = async () => {
    try {
      const [b, t, c] = await Promise.all([
        api.get("/white-label/branding"),
        api.get("/white-label/templates"),
        api.get("/reports/catalogue"),
      ]);
      setBranding(b.data || {});
      setTemplates(t.data.items || []);
      setCatalogue(c.data.items || []);
      setDirty(false);
      if (!newTpl.report_type && c.data.items?.length) setNewTpl((p) => ({ ...p, report_type: c.data.items[0].key }));
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(
        Object.entries(branding).filter(([k]) => !["id", "org_id", "created_at", "updated_at"].includes(k))
      );
      await api.put("/white-label/branding", clean);
      altioraToast.success(lang === "fr" ? "Identité enregistrée" : "Branding saved");
      setDirty(false);
    } catch {
      altioraToast.error(lang === "fr" ? "Erreur d'enregistrement" : "Save error");
    } finally { setSaving(false); }
  };

  const set = (name, value) => { setBranding((b) => ({ ...b, [name]: value })); setDirty(true); };

  const createTemplate = async () => {
    if (!newTpl.name.trim() || !newTpl.report_type) return;
    try {
      await api.post("/white-label/templates", newTpl);
      setNewTpl({ name: "", sector: "employability", report_type: catalogue[0]?.key || "" });
      await load();
      altioraToast.success(lang === "fr" ? "Modèle créé" : "Template created");
    } catch {
      altioraToast.error(lang === "fr" ? "Erreur de création" : "Create error");
    }
  };

  const deleteTemplate = async (id) => {
    if (!confirm(lang === "fr" ? "Supprimer ce modèle ?" : "Delete this template?")) return;
    await api.delete(`/white-label/templates/${id}`);
    await load();
  };

  return (
    <div className="min-h-full" data-testid="whitelabel-root">
      <TopBar
        title={lang === "fr" ? "White Label Studio" : "White Label Studio"}
        subtitle={lang === "fr" ? "Personnalisez tous les rapports — logo, couleurs, mentions, signatures, QR, filigrane." : "Customise every report — logo, colours, mentions, signatures, QR, watermark."}
      />
      <div className="px-6 md:px-10 py-8 md:py-10 space-y-8">
        {/* Branding */}
        <div className="rounded-md border border-[#E4E1D8] bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E1D8]">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
              <div className="font-display text-lg text-[#1F2937] tracking-tight">
                {lang === "fr" ? "Identité de l'organisation" : "Organisation branding"}
              </div>
            </div>
            <button
              data-testid="whitelabel-save"
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-40 text-white text-xs font-medium px-4 py-2 rounded-md transition-colors"
            >
              <Save className="h-3.5 w-3.5" strokeWidth={1.8} />
              {saving ? "..." : (lang === "fr" ? "Enregistrer" : "Save")}
            </button>
          </div>
          <div className="p-6 space-y-8">
            {FIELD_GROUPS.map((group) => (
              <div key={group.key}>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-3">
                  {group[`label_${lang}`]}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {group.fields.map((f) => (
                    <div key={f.name} className={f.type === "textarea" ? "md:col-span-3" : ""}>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                        {f[`label_${lang}`]}
                      </label>
                      {f.type === "color" ? (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            data-testid={`whitelabel-${f.name}`}
                            type="color"
                            value={branding[f.name] || "#0B2545"}
                            onChange={(e) => set(f.name, e.target.value)}
                            className="h-9 w-14 rounded border border-[#E4E1D8] bg-[#F8F7F3] cursor-pointer"
                          />
                          <input
                            type="text"
                            value={branding[f.name] || ""}
                            onChange={(e) => set(f.name, e.target.value)}
                            className="flex-1 bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
                          />
                        </div>
                      ) : f.type === "textarea" ? (
                        <textarea
                          data-testid={`whitelabel-${f.name}`}
                          value={branding[f.name] || ""}
                          onChange={(e) => set(f.name, e.target.value)}
                          rows={3}
                          className="mt-2 w-full bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] focus:border-[#0B2545] focus:outline-none"
                        />
                      ) : (
                        <input
                          data-testid={`whitelabel-${f.name}`}
                          type="text"
                          value={branding[f.name] || ""}
                          onChange={(e) => set(f.name, e.target.value)}
                          className="mt-2 w-full bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] focus:border-[#0B2545] focus:outline-none"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Templates */}
        <div className="rounded-md border border-[#E4E1D8] bg-white">
          <div className="px-6 py-4 border-b border-[#E4E1D8] font-display text-lg text-[#1F2937] tracking-tight">
            {lang === "fr" ? "Modèles de rapports" : "Report templates"}
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                data-testid="whitelabel-tpl-name"
                type="text"
                placeholder={lang === "fr" ? "Nom du modèle" : "Template name"}
                value={newTpl.name}
                onChange={(e) => setNewTpl((p) => ({ ...p, name: e.target.value }))}
                className="bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] focus:border-[#0B2545] focus:outline-none"
              />
              <select
                data-testid="whitelabel-tpl-sector"
                value={newTpl.sector}
                onChange={(e) => setNewTpl((p) => ({ ...p, sector: e.target.value }))}
                className="bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
              >
                {Object.entries(SECTOR_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l[lang]}</option>
                ))}
              </select>
              <select
                data-testid="whitelabel-tpl-type"
                value={newTpl.report_type}
                onChange={(e) => setNewTpl((p) => ({ ...p, report_type: e.target.value }))}
                className="bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] font-mono focus:border-[#0B2545] focus:outline-none"
              >
                {catalogue.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <button
                data-testid="whitelabel-tpl-create"
                onClick={createTemplate}
                className="inline-flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#DCC29A] text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                <PlusCircle className="h-4 w-4" strokeWidth={1.8} />
                {lang === "fr" ? "Créer" : "Create"}
              </button>
            </div>

            {templates.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.map((t) => (
                  <div key={t.template_id} className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] p-4 flex items-start justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#1F2937]">{t.name}</div>
                      <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                        <span>{SECTOR_LABELS[t.sector]?.[lang] || t.sector}</span>
                        <span className="text-[#5B5B62]">·</span>
                        <span className="text-[#0B2545]">{t.report_type}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteTemplate(t.template_id)}
                      className="text-[#8B0000] hover:text-[#D97706]"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
